import type { SourceItem } from '@mg/shared';
import { ExternalLink } from 'lucide-react';
import { formatRelative } from '../../lib/display';

/** Presentational list of ingested source items — shared by the source and theme viewers. */
export function SourceItemList({ items }: { items: SourceItem[] }) {
  return (
    <ul className="divide-y divide-line-soft">
      {items.map((item) => (
        <li key={item.id} className="py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              {item.url ? (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  className="focus-ring inline-flex items-start gap-1 text-sm font-medium text-link hover:text-action-hover hover:underline"
                >
                  <span className="min-w-0">{item.title}</span>
                  <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                </a>
              ) : (
                <span className="text-sm font-medium text-ink">{item.title}</span>
              )}
              {item.summary && (
                <p className="mt-1 line-clamp-2 text-sm text-ink-muted">{item.summary}</p>
              )}
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-faint">
                {item.sourceName && (
                  <span className="inline-flex items-center rounded-full bg-tint px-2 py-0.5 font-medium text-tint-fg">
                    {item.sourceName}
                  </span>
                )}
                {item.author && <span className="truncate">{item.author}</span>}
                {item.author && (item.publishedAt || item.pulledAt) && <span>·</span>}
                {item.publishedAt ? (
                  <span>Published {formatRelative(item.publishedAt)}</span>
                ) : (
                  <span>Pulled {formatRelative(item.pulledAt)}</span>
                )}
              </div>
              {item.raw != null && (
                <details className="mt-2">
                  <summary className="focus-ring inline-block cursor-pointer rounded text-xs font-medium text-ink-muted hover:text-ink-strong">
                    Show raw payload
                  </summary>
                  <pre className="mt-1.5 max-h-72 overflow-auto rounded-lg bg-chrome-control p-3 text-[0.6875rem] leading-relaxed text-chrome-text">
                    {JSON.stringify(item.raw, null, 2)}
                  </pre>
                </details>
              )}
            </div>
            {item.imageUrl && (
              <img
                src={item.imageUrl}
                alt=""
                className="h-12 w-12 shrink-0 rounded-lg bg-white object-cover"
                loading="lazy"
              />
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
